import { ReactElement, useCallback, useEffect, useState } from 'react'
import { Prompt } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { fetchHomeEnergyHistoryThunk } from 'features/home/components/HomeConfiguration/slice'
import useTimeoutPoll from 'app/utils/useTimeoutPoll'
import { Home } from 'features/home/types'
import { CommonButton } from 'components/Button'
import { mdiAlertCircleOutline } from '@mdi/js'
import {
  HomeLogsButton,
  HomeLogsSpinnerContainer,
  StyledHomeLogsHeader,
  StyledHomeLogsInfo,
  StyledHomeLogsInfoName,
  StyledLogsContentBtnsWrap,
  StyledCheckBoxRow,
  StyledResolutionTitle,
  RadioFieldItemWrapper,
  RadioFieldTextWrapper,
  AllHistoryWrapper,
} from './styles'
import SuccessModal from 'components/Modal/SuccessModal'
import { energyUploadUnsupportedHostVersion } from '../utils'
import { homeLogsMessages } from '../messages'
import { commonMessages } from 'app/i18n/messages'
import { SpinnerCentered } from 'components/Loader'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import { DatePicker } from 'components/Input'
import { fetchHomeActiveConfigEnergyDataThunk } from 'features/home/components/HomeLogs/EnergyHistoryContent/slice'
import { homeActiveConfigEnergyDataSelector } from 'features/home/selectors'
import { fetchFile } from 'app/utils/fetchFile'
import FileSaver from 'file-saver'
import { ErrorIcon } from 'components/Table/Cell/DownloadButtonCell/styles'
import { WhiteBlock } from 'components/Layout'
import { Checkbox } from 'components/Input'
import RadioField from './RadioField'

const THREE_MINUTES = 1000 * 60 * 3

interface HomeLogsContentProps {
  home: Home
}

function EnergyHistoryContent({ home }: HomeLogsContentProps) {
  const { formatMessage } = useIntl()

  const dispatch = useAppDispatch()
  const [showTimeout, setShowTimeout] = useState(false)
  const [errorCode, setErrorCode] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [lastUpdated, setLastUpdated] = useState('')

  const handleShowTimeout = useCallback(() => setShowTimeout(true), [])

  const [polling, setPolling] = useTimeoutPoll(THREE_MINUTES, handleShowTimeout)

  const isHomeActiveConfigEnergyDataLoaded = useAppSelector(
    homeActiveConfigEnergyDataSelector.isFinished
  )
  const homeActiveConfigDataEnergy = useAppSelector(
    homeActiveConfigEnergyDataSelector.data
  )

  const [dateStart, setDateStart] = useState(0)
  const [dateEnd, setDateEnd] = useState(0)
  const [granularity, setGranularity] = useState('')
  const [isAllHistory, setIsAllHistory] = useState(false)
  const [checked, setIsChecked] = useState({
    optionFive: false,
    optionFifteen: false,
    optionSixty: false,
  })
  const [includeLoadControlEvents, setIncludeLoadControlEvents] =
    useState(false)

  useEffect(() => {
    if (home.id && !isHomeActiveConfigEnergyDataLoaded) {
      dispatch(fetchHomeActiveConfigEnergyDataThunk(home.id, false))
    }
  }, [dispatch, home.id, isHomeActiveConfigEnergyDataLoaded, polling])

  useEffect(() => {
    if (home.id && polling) {
      dispatch(fetchHomeActiveConfigEnergyDataThunk(home.id, false))
    }

    if (isHomeActiveConfigEnergyDataLoaded && homeActiveConfigDataEnergy !== null) {
      if (homeActiveConfigDataEnergy?.payload !== null && homeActiveConfigDataEnergy?.payload.updated.toString() !== lastUpdated) {
        setPolling(false)
      }
    }
  }, [dispatch, home.id, polling, lastUpdated, homeActiveConfigDataEnergy, setPolling, isHomeActiveConfigEnergyDataLoaded])

  function formatTimestamp(timestamp: number | undefined) {
    try {
      if (timestamp !== undefined) {
        const milliseconds = timestamp
        const dateObject = new Date(milliseconds)
        const humanDateFormat = dateObject.toLocaleString()
        return humanDateFormat
      } else {
        return '-'
      }
    } catch (e) {
      console.log(e)
      return timestamp
    }
  }


  const handleChangeIsAllHistoryCheckbox = () => {
    setIsAllHistory(!isAllHistory)
    if (!isAllHistory) {
      setDateStart(1)
      setDateEnd(1)
    } else {
      setDateStart(0)
      setDateEnd(0)
    }
  }

  const handleChangeIncludeLoadControlEventsCheckbox = () => {
    // TODO
    setIncludeLoadControlEvents(!includeLoadControlEvents)
    if (!includeLoadControlEvents) {
    } else {
    }
  }

  const handleGranularityCheckbox = (e: any) => {
    const granularityValue = e.target.value
    setGranularity(granularityValue)

    switch (granularityValue) {
      case '5-minute':
        setIsChecked({
          optionFive: true,
          optionFifteen: false,
          optionSixty: false,
        })
        return
      case '15-minute':
        setIsChecked({
          optionFive: false,
          optionFifteen: true,
          optionSixty: false,
        })
        return
      case '60-minute':
        setIsChecked({
          optionFive: false,
          optionFifteen: false,
          optionSixty: true,
        })
        return
    }
  }

  const handleDownloadClick = async () => {
    setIsLoading(true)
    try {
      const name = homeActiveConfigDataEnergy?.payload.name || 'energy.tar.gz'
      const url = homeActiveConfigDataEnergy?.payload.location.url || ''
      const { data } = await fetchFile({
        url,
        responseType: 'blob',
        contentType: 'application/octet-stream',
      })

      FileSaver.saveAs(data, name + '.tar.gz')
    } catch (error) {
      setShowError(true)
      console.log('Error downloading file', error)
    } finally {
      setIsLoading(false)
    }
  }

  const hostType = home.hostType
  const versionNumber = home.softwareInfo?.version.number || 0

  const unsupportedHostVersion = energyUploadUnsupportedHostVersion(
    hostType,
    versionNumber
  )

  const monitorAccessEnabled = home.monitorAccessEnabled

  const shouldDisableButtons =
    polling ||
    !unsupportedHostVersion ||
    !monitorAccessEnabled ||
    (!isAllHistory && !dateStart && !dateEnd) ||
    !dateStart ||
    !dateEnd ||
    !granularity

  const renderFetchButton = (): ReactElement => {
    const handleFetchClick = async () => {
      try {
        setLastUpdated(homeActiveConfigDataEnergy?.payload?.updated.toString() || '')

        let dateStartIso, dateEndIso

        if (isAllHistory) {
          dateStartIso = ((d) =>
            new Date(d.getFullYear() - 10, d.getMonth(), d.getDate()))(
              new Date()
            )
          dateEndIso = ((d) =>
            new Date(d.getFullYear() + 1, d.getMonth(), d.getDate()))(
              new Date()
            )
        } else {
          dateStartIso = new Date(dateStart)
          dateEndIso = new Date(dateEnd)
        }

        dispatch(
          fetchHomeEnergyHistoryThunk(
            home.id,
            granularity,
            dateStartIso.toISOString(),
            dateEndIso.toISOString()
          )
        )

        //setInterval(checkEnergyLogs, 5000)

        setPolling(true)
      } catch (error) {
        console.error(error)
      }
    }

    return (
      <div style={{ paddingRight: '20px' }}>
        <HomeLogsButton
          variant="primary"
          onClick={handleFetchClick}
          disabled={shouldDisableButtons}
        >
          {polling
            ? formatMessage(homeLogsMessages.fetching)
            : 'Get Data'}
        </HomeLogsButton>
      </div>
    )
  }

  if (!isHomeActiveConfigEnergyDataLoaded) {
    return (
      <HomeLogsSpinnerContainer>
        <SpinnerCentered size="40" />
      </HomeLogsSpinnerContainer>
    )
  }

  return (
    <>
      <Prompt
        when={polling}
        message={formatMessage(homeLogsMessages.fetchingLeave)}
      />
      <div>
        {errorCode && (
          <SuccessModal
            title={formatMessage(commonMessages.error)}
            isOpen
            confirmBtnTitle={formatMessage(commonMessages.ok)}
            onConfirm={() => setErrorCode(null)}
          >
            {formatMessage(
              errorCode === 234
                ? homeLogsMessages.notAvailableHost
                : commonMessages.commonError
            )}
          </SuccessModal>
        )}
        {showTimeout && polling && (
          <>
            <SuccessModal
              title={formatMessage(homeLogsMessages.timedOutTitle)}
              isOpen={showTimeout}
              confirmBtnTitle={formatMessage(commonMessages.ok)}
              onConfirm={() => setShowTimeout(false)}
            />
            <div>
              <FormattedMessage {...homeLogsMessages.timedOut} />
              <CommonButton onClick={() => setShowTimeout(false)}>
                <FormattedMessage {...commonMessages.ok} />
              </CommonButton>
            </div>
          </>
        )}
        <StyledHomeLogsHeader>
          <StyledHomeLogsInfo>
            <h1>Energy History</h1>
          </StyledHomeLogsInfo>
        </StyledHomeLogsHeader>
        <div
          style={{
            display: 'flex',
            paddingRight: '10px',
          }}
        >
          <DatePicker
            value={dateStart}
            onChange={(value) => setDateStart(value)}
            label="Start Time"
            disabled={isAllHistory}
          />
          <DatePicker
            value={dateEnd}
            onChange={(value) => setDateEnd(value)}
            label="End Time"
            disabled={isAllHistory}
          />
          <AllHistoryWrapper>
            <StyledCheckBoxRow>
              <Checkbox onChange={handleChangeIsAllHistoryCheckbox} /> All
              History
            </StyledCheckBoxRow>
          </AllHistoryWrapper>
        </div>

        <StyledResolutionTitle>Data Resolution</StyledResolutionTitle>

        <div
          style={{
            display: 'flex',
            paddingBottom: '10px',
            paddingLeft: '10px',
          }}
        >
          <RadioFieldItemWrapper>
            <RadioField
              name="radioFieldFive"
              value="5-minute"
              isChecked={checked.optionFive}
              onChange={handleGranularityCheckbox}
            />
            <RadioFieldTextWrapper>5 minutes</RadioFieldTextWrapper>
          </RadioFieldItemWrapper>
          <RadioFieldItemWrapper>
            <RadioField
              name="radioFieldFifteen"
              value="15-minute"
              isChecked={checked.optionFifteen}
              onChange={handleGranularityCheckbox}
            />
            <RadioFieldTextWrapper>15 minutes</RadioFieldTextWrapper>
          </RadioFieldItemWrapper>
          <RadioFieldItemWrapper>
            <RadioField
              name="radioFieldSixty"
              value="60-minute"
              isChecked={checked.optionSixty}
              onChange={handleGranularityCheckbox}
            />
            <RadioFieldTextWrapper>60 minutes</RadioFieldTextWrapper>
          </RadioFieldItemWrapper>
        </div>

        <div
          style={{
            display: 'flex',
            paddingTop: '20px',
            paddingBottom: '20px',
            paddingLeft: '10px',
          }}
        >
          <StyledLogsContentBtnsWrap>
            {renderFetchButton()}
          </StyledLogsContentBtnsWrap>
          {false && (
            <StyledCheckBoxRow>
              <Checkbox
                onChange={handleChangeIncludeLoadControlEventsCheckbox}
              />{' '}
              Include Load Control Events (Last 30 days)
            </StyledCheckBoxRow>
          )}
        </div>

        {!unsupportedHostVersion && (
          <WhiteBlock bigPadding>
            Energy History is only supported on Director Hosts 11.1 and Mac Hosts 11.2 or Greater
          </WhiteBlock>
        )}
        {unsupportedHostVersion && (
          <WhiteBlock bigPadding>
            {homeActiveConfigDataEnergy === undefined ||
              (homeActiveConfigDataEnergy?.payload === null && (
                <HomeLogsSpinnerContainer>
                  No recent log captures available
                </HomeLogsSpinnerContainer>
              ))}
            {homeActiveConfigDataEnergy !== undefined &&
              homeActiveConfigDataEnergy?.payload !== null && (
                <HomeLogsSpinnerContainer>

                  <StyledHomeLogsInfoName>
                    File Name: {homeActiveConfigDataEnergy?.payload.name}.csv
                  </StyledHomeLogsInfoName>
                  <StyledHomeLogsInfoName>
                    Updated: {formatTimestamp(homeActiveConfigDataEnergy?.payload.updated)}
                  </StyledHomeLogsInfoName>

                  <HomeLogsButton
                    variant="primary"
                    onClick={handleDownloadClick}
                  >
                    <FormattedMessage {...commonMessages.download} />
                    {isLoading && <SpinnerCentered size="16" />}
                    {showError && (
                      <ErrorIcon
                        path={mdiAlertCircleOutline}
                        title={formatMessage(commonMessages.commonError)}
                        description={formatMessage(commonMessages.commonError)}
                        size={0.8}
                      />
                    )}
                  </HomeLogsButton>
                </HomeLogsSpinnerContainer>
              )}
          </WhiteBlock>
        )}
      </div>
    </>
  )
}

export default EnergyHistoryContent
